/**
 * The genogram and ecomap vocabularies, mirrored from the backend.
 *
 * Mirrored rather than derived, the way the constellation module mirrors its
 * figure catalogue: the backend is the authority on what a board may hold, and
 * this copy exists so the palette can grey out an illegal link before the
 * server rejects it. A value added here and not there is a control that fails
 * on save; `src/modules/systemic_map/domain/__tests__/types.test.ts` pins the
 * two lists together so that cannot happen quietly.
 *
 * The symbol sets are not ours to invent — a genogram drawn with made-up
 * symbols cannot be read by the next clinician:
 *
 * - Genogram: McGoldrick, Gerson & Petry. Square male, circle female, diamond
 *   unknown, triangle pregnancy; a cross for the deceased and a double outline
 *   for the index patient. Unions carry their separation and divorce slashes,
 *   filiation covers adoption, twins and the pregnancy losses, and the
 *   emotional layer (close, fused, distant, hostile, cutoff and the compound
 *   forms) is what turns a family tree into a clinical instrument.
 * - Ecomap: Hartman. One centre, the systems around it, and each tie carrying
 *   two independent qualities — how strong it is, and which way energy runs.
 */

export const MAP_KINDS = ["genogram", "ecomap"] as const;
export type MapKind = (typeof MAP_KINDS)[number];

export const NODE_TYPES = ["person", "centre", "system", "note"] as const;
export type NodeType = (typeof NODE_TYPES)[number];

export const LINK_TYPES = ["union", "child", "emotional", "system"] as const;
export type LinkType = (typeof LINK_TYPES)[number];

export const PERSON_GENDERS = ["male", "female", "unknown", "pregnancy"] as const;
export type PersonGender = (typeof PERSON_GENDERS)[number];

export const UNION_TYPES = [
    "married",
    "cohabiting",
    "engaged",
    "separated",
    "divorced",
    "annulled",
    "ended",
] as const;
export type UnionType = (typeof UNION_TYPES)[number];

export const CHILD_TYPES = [
    "biological",
    "adopted",
    "foster",
    "step",
    "twin",
    "identicalTwin",
    "miscarriage",
    "stillbirth",
    "termination",
] as const;
export type ChildType = (typeof CHILD_TYPES)[number];

export const EMOTIONAL_TYPES = [
    "close",
    "fused",
    "distant",
    "hostile",
    "cutoff",
    // "Quiebre": the line barred through. Listed beside `cutoff` because the
    // Spanish sets therapists here work from carry both.
    "broken",
    "fusedHostile",
    "closeHostile",
    "neglect",
    "abuse",
] as const;
export type EmotionalType = (typeof EMOTIONAL_TYPES)[number];

export const SYSTEM_CATEGORIES = [
    "family",
    "work",
    "school",
    "friends",
    "health",
    "spirituality",
    "leisure",
    "community",
    "socialServices",
    "finances",
    "custom",
] as const;
export type SystemCategory = (typeof SYSTEM_CATEGORIES)[number];

export const LINK_STRENGTHS = ["strong", "tenuous", "stressful"] as const;
export type LinkStrength = (typeof LINK_STRENGTHS)[number];

export const ENERGY_DIRECTIONS = ["towardPerson", "towardSystem", "both"] as const;
export type EnergyDirection = (typeof ENERGY_DIRECTIONS)[number];

/**
 * A therapist's own colour on an element.
 *
 * A closed set rather than free hex: the board is read in two themes and by
 * people with colour-vision deficiency, and these are the palette the design
 * standards already validate. `default` is the board's own ink.
 */
export const ELEMENT_COLOURS = [
    "default",
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
] as const;
export type ElementColour = (typeof ELEMENT_COLOURS)[number];

/** What each colour is, in CSS. Kept beside the list it belongs to. */
export const COLOUR_INK: Record<ElementColour, string | undefined> = {
    default: undefined,
    one: "var(--chart-1)",
    two: "var(--chart-2)",
    three: "var(--chart-3)",
    four: "var(--chart-4)",
    five: "var(--chart-5)",
    six: "var(--chart-6)",
};

/** The ecomap centre always carries this id, fixed by the backend. */
export const CENTRE_NODE_ID = "centre";

/** Bounds mirrored from the backend, so a control can stop before a 422. */
export const MAX_NODES = 120;
export const MAX_HOUSEHOLD_MEMBERS = 30;
export const MIN_CENTRE_RADIUS = 90;
export const MAX_CENTRE_RADIUS = 900;
export const DEFAULT_CENTRE_RADIUS = 230;
/** How much one press of expand or contract moves it. */
export const CENTRE_RADIUS_STEP = 60;
export const MIN_SYSTEM_RADIUS = 14;
export const MAX_SYSTEM_RADIUS = 160;
export const DEFAULT_SYSTEM_RADIUS = 22;
export const MAX_OPERATIONS = 100;
export const MAX_TITLE_LENGTH = 80;
export const MAX_NAME_LENGTH = 60;
export const MAX_LABEL_LENGTH = 60;
export const MAX_NOTE_LENGTH = 280;
export const MIN_YEAR = 1600;
export const MAX_YEAR = 2200;

/** Every element may carry the therapist's own colour. */
export interface Coloured {
    colour?: ElementColour;
}

/** Who last touched an element. Display and provenance only, never authority. */
export interface Attribution {
    updatedAt?: string;
    updatedBy?: string;
    updatedByType?: "therapist" | "participant";
    updatedByName?: string;
}

export interface PersonNode extends Attribution, Coloured {
    id: string;
    type: "person";
    gender: PersonGender;
    name: string;
    birthYear: number | null;
    deathYear: number | null;
    deceased: boolean;
    /** The person the family is being read around. At most one per board. */
    index: boolean;
    notes: string;
    x: number;
    y: number;
}

export interface CentreNode extends Attribution, Coloured {
    id: string;
    type: "centre";
    label: string;
    /**
     * The ring the household is drawn inside.
     *
     * Hartman's ecomap is a circle with the family in it and the systems
     * around it, so the circle has to grow with the family — and stop before
     * it swallows the systems.
     */
    radius?: number;
    x: number;
    y: number;
}

export interface SystemNode extends Attribution, Coloured {
    id: string;
    type: "system";
    system: SystemCategory;
    label: string;
    /**
     * How large it is drawn.
     *
     * Size is meaning on an ecomap: a family that fills someone's life is not
     * the same size as their Tuesday shift.
     */
    radius?: number;
    x: number;
    y: number;
}

/**
 * A note pinned to the board.
 *
 * Not notation: something the two people in the room wanted written down
 * where the drawing is. `author` says who put it there, which is not the same
 * as who last touched it — the other person may well move it out of the way.
 * The server sets it; a client cannot claim someone else's words.
 */
export interface NoteNode extends Attribution, Coloured {
    id: string;
    type: "note";
    text: string;
    author?: string;
    authorType?: "therapist" | "participant";
    x: number;
    y: number;
}

export type MapNode = PersonNode | CentreNode | SystemNode | NoteNode;

export interface UnionLink extends Attribution, Coloured {
    id: string;
    type: "union";
    from: string;
    to: string;
    union: UnionType;
    since: number | null;
    until: number | null;
}

export interface ChildLink extends Attribution, Coloured {
    id: string;
    type: "child";
    /** A union, not a person: children hang off the couple's line. */
    from: string;
    to: string;
    child: ChildType;
}

export interface EmotionalLink extends Attribution, Coloured {
    id: string;
    type: "emotional";
    from: string;
    to: string;
    emotional: EmotionalType;
}

export interface SystemLink extends Attribution, Coloured {
    id: string;
    type: "system";
    from: string;
    to: string;
    strength: LinkStrength;
    energy: EnergyDirection;
}

export type MapLink = UnionLink | ChildLink | EmotionalLink | SystemLink;

export interface Household extends Attribution {
    id: string;
    label: string;
    members: string[];
}

export interface MapState {
    schemaVersion: number;
    kind: MapKind;
    nodes: MapNode[];
    links: MapLink[];
    households: Household[];
    tombstones?: string[];
    meta: { legendVisible: boolean; notes: string };
}

export interface SystemicMap {
    id: string;
    kind: MapKind;
    title: string;
    patient_id: string | null;
    couple_patient_id: string | null;
    group_patient_id?: string | null;
    state: MapState;
    version: number;
    snapshot_urls: string[];
    /** When the therapist opened this map to the patient's portal, if ever. */
    shared_with_patient_at: string | null;
    created_at: string;
    updated_at: string;
}

/** A row in the map list: what the row shows, without the board. */
export interface SystemicMapSummary {
    id: string;
    kind: MapKind;
    title: string;
    patient_id: string | null;
    couple_patient_id: string | null;
    group_patient_id?: string | null;
    element_count: number;
    version: number;
    shared_with_patient_at: string | null;
    updated_at: string;
    /** Who the map is about, resolved by the server. Null for an open board. */
    subject_name: string | null;
}

/** What the patient's client sees: the board, and nothing about the record. */
export interface PublicSystemicMap {
    id: string;
    kind: MapKind;
    title: string;
    state: MapState;
    version: number;
}

/** A row in the patient's own list of the maps their therapist shared. */
export interface PortalMapSummary {
    id: string;
    kind: MapKind;
    title: string;
    element_count: number;
    updated_at: string;
    shared_at: string;
}

export interface ShareLink {
    token: string;
    url: string;
    expires_at: string;
}

export interface JoinResponse {
    participant_id: string;
    map: PublicSystemicMap;
}

export const isPerson = (node: MapNode): node is PersonNode => node.type === "person";
export const isSystem = (node: MapNode): node is SystemNode => node.type === "system";
export const isCentre = (node: MapNode): node is CentreNode => node.type === "centre";
export const isNote = (node: MapNode): node is NoteNode => node.type === "note";
